import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react'
import type { User, Session } from '@supabase/supabase-js'
import { supabase, waitForSupabaseReady } from '@/lib/supabase'
import { Capacitor } from '@capacitor/core'
import { App } from '@capacitor/app'
import { Browser } from '@capacitor/browser'
// Note: Using OAuth flow for Apple Sign In (same as Google) for simplicity
// Native Sign in with Apple plugin available but requires cap sync
import { cleanupNativeFeatures } from '@/lib/native'
import { clearWidgetAuthToken } from '@/lib/native/widget-data'
import { loginPurchases, logoutPurchases } from '@/lib/native/purchases'
import { clearSubscriptionCache } from '@/lib/hooks/use-subscription'

/**
 * Update the user's platform field in their profile.
 * Called on sign in / app open so we always have the latest platform.
 */
async function updateUserPlatform(userId: string): Promise<void> {
  try {
    const platform = Capacitor.getPlatform() as 'ios' | 'android' | 'web'
    await supabase
      .from('user_profiles')
      .update({ platform })
      .eq('id', userId)
  } catch (error) {
    console.warn('[updateUserPlatform] Failed:', error)
  }
}

/**
 * Detect if running on iPad.
 * iPad requires fullscreen presentation for OAuth to work reliably.
 * On iPad, popover-style browsers can be dismissed by tapping outside,
 * which interrupts the OAuth flow.
 */
function isIPad(): boolean {
  if (Capacitor.getPlatform() !== 'ios') return false
  
  // Check for iPad in user agent
  if (/iPad/.test(navigator.userAgent)) return true
  
  // Modern iPads report as Macintosh with touch support
  // Check for multi-touch + Macintosh user agent (iPad in desktop mode)
  if (navigator.maxTouchPoints > 1 && /Macintosh/.test(navigator.userAgent)) return true
  
  return false
}

/**
 * Get the appropriate browser presentation style for the current device.
 * iPad needs fullscreen to prevent accidental dismissal during OAuth.
 */
function getBrowserPresentationStyle(): 'popover' | 'fullscreen' {
  return isIPad() ? 'fullscreen' : 'popover'
}

// OAuth timeout in milliseconds (60 seconds)
const OAUTH_TIMEOUT_MS = 60000

interface AuthContextValue {
  user: User | null
  session: Session | null
  loading: boolean
  oauthInProgress: boolean
  oauthError: string | null
  clearOAuthError: () => void
  signInWithGoogle: () => Promise<void>
  signInWithApple: () => Promise<void>
  signInWithEmail: (email: string, password: string) => Promise<{ error?: string }>
  signUpWithEmail: (email: string, password: string) => Promise<{ error?: string; needsConfirmation?: boolean }>
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

/**
 * Ensures a user profile exists in the database.
 * Creates one if it doesn't exist (for new users).
 * This is called after successful authentication.
 */
async function ensureUserProfile(user: User): Promise<void> {
  try {
    // Wait for Supabase to be fully ready before making queries
    await waitForSupabaseReady()
    
    // Check if profile already exists
    const { data: existingProfile, error: fetchError } = await supabase
      .from('user_profiles')
      .select('id, onboarding_completed_at')
      .eq('id', user.id)
      .single()
    
    if (existingProfile) {
      return
    }
    
    // Profile doesn't exist - create it
    // PGRST116 means "no rows returned" which is expected for new users
    if (fetchError && fetchError.code !== 'PGRST116') {
      console.error('[ensureUserProfile] Error checking profile:', fetchError.message, fetchError.code)
      // Don't throw - we'll try to create anyway
    }
    
    // Extract display name from OAuth metadata or email
    const displayName = 
      user.user_metadata?.full_name ||
      user.user_metadata?.name ||
      user.email?.split('@')[0] ||
      'User'
    
    const platform = Capacitor.getPlatform() as 'ios' | 'android' | 'web'
    
    // 14-day free Pro trial for new users
    const trialExpiresAt = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString()
    
    const { error: insertError } = await supabase
      .from('user_profiles')
      .insert({
        id: user.id,
        display_name: displayName,
        currency_shards: 50, // Welcome bonus
        onboarding_step: 'new',
        platform,
        // 14-day free Pro trial — no credit card, auto-expires
        subscription_tier: 'premium',
        subscription_status: 'trialing',
        subscription_expires_at: trialExpiresAt,
        personalization_data: { pro_trial_shown: false },
        // onboarding_completed_at is left null - will be set when they complete onboarding
      })
    
    if (insertError) {
      // Could be a race condition where profile was created by another request
      if (insertError.code === '23505') { // Unique violation
        return
      }
      console.error('[ensureUserProfile] Error creating profile:', insertError.message, insertError.code)
      throw insertError
    }
    
  } catch (error) {
    console.error('[ensureUserProfile] Failed to ensure profile:', error)
    // Don't throw - we don't want to block the user from using the app
    // The WelcomePage will handle the case where profile doesn't exist
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)
  const [oauthInProgress, setOAuthInProgress] = useState(false)
  const [oauthError, setOAuthError] = useState<string | null>(null)
  
  // Track which users we've already ensured profiles for (to avoid redundant calls)
  const ensuredProfiles = useRef<Set<string>>(new Set())
  // Track if initAuth has already been called (for StrictMode)
  const initAuthCalled = useRef(false)
  // Track if auth state change already set loading to false
  const loadingSetByAuthChange = useRef(false)
  // Track OAuth timeout
  const oauthTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Track if OAuth is in progress (ref for use in callbacks that might have stale closures)
  const oauthInProgressRef = useRef(false)
  
  // Clear OAuth error
  const clearOAuthError = useCallback(() => {
    setOAuthError(null)
  }, [])
  
  // Cancel OAuth timeout
  const cancelOAuthTimeout = useCallback(() => {
    if (oauthTimeoutRef.current) {
      clearTimeout(oauthTimeoutRef.current)
      oauthTimeoutRef.current = null
    }
  }, [])
  
  // Start OAuth with timeout
  const startOAuthFlow = useCallback(() => {
    setOAuthInProgress(true)
    oauthInProgressRef.current = true
    setOAuthError(null)
    cancelOAuthTimeout()
    
    // Set timeout for OAuth flow
    oauthTimeoutRef.current = setTimeout(() => {
      // Use ref to check current state (avoids stale closure)
      if (oauthInProgressRef.current) {
        console.warn('OAuth flow timed out')
        setOAuthInProgress(false)
        oauthInProgressRef.current = false
        setOAuthError('Sign in timed out. Please try again.')
        // Try to close the browser if it's still open
        if (Capacitor.isNativePlatform()) {
          Browser.close().catch(() => {})
        }
      }
    }, OAUTH_TIMEOUT_MS)
  }, [cancelOAuthTimeout])
  
  // Complete OAuth flow (success or failure)
  const completeOAuthFlow = useCallback((error?: string) => {
    cancelOAuthTimeout()
    setOAuthInProgress(false)
    oauthInProgressRef.current = false
    if (error) {
      setOAuthError(error)
    }
  }, [cancelOAuthTimeout])

  // Handle deep link OAuth callback
  const handleDeepLink = useCallback(async (url: string) => {
    // Check if this is an auth callback
    if (url.includes('auth/callback') || url.includes('access_token') || url.includes('code=')) {
      try {
        // Parse the URL to extract tokens or code
        const urlObj = new URL(url)
        
        // Check for OAuth error in callback
        const errorParam = urlObj.searchParams.get('error')
        const errorDescription = urlObj.searchParams.get('error_description')
        if (errorParam) {
          console.error('OAuth error in callback:', errorParam, errorDescription)
          completeOAuthFlow(errorDescription || errorParam || 'Authentication failed')
          // Close the browser
          if (Capacitor.isNativePlatform()) {
            try {
              await Browser.close()
            } catch (e) {
              // Browser might already be closed
            }
          }
          return
        }
        
        let authHandled = false
        
        // Check for hash-based tokens (implicit flow)
        if (urlObj.hash && !authHandled) {
          const hashParams = new URLSearchParams(urlObj.hash.substring(1))
          const accessToken = hashParams.get('access_token')
          const refreshToken = hashParams.get('refresh_token')
          
          if (accessToken) {
            authHandled = true
            const { data, error } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken || '',
            })
            
            if (error) {
              console.error('Error setting session:', error)
              completeOAuthFlow(error.message)
            } else {
              setSession(data.session)
              setUser(data.session?.user ?? null)
              completeOAuthFlow() // Success
            }
          }
        }
        
        // Check for code-based tokens (PKCE flow) - only if hash flow wasn't used
        const code = urlObj.searchParams.get('code')
        if (code && !authHandled) {
          authHandled = true
          const { data, error } = await supabase.auth.exchangeCodeForSession(code)
          
          if (error) {
            console.error('Error exchanging code for session:', error)
            completeOAuthFlow(error.message)
          } else {
            setSession(data.session)
            setUser(data.session?.user ?? null)
            completeOAuthFlow() // Success
          }
        }
        
        // Close the in-app browser on native platforms
        if (Capacitor.isNativePlatform()) {
          try {
            await Browser.close()
          } catch (e) {
            // Browser might already be closed
          }
        }
      } catch (error) {
        console.error('Error handling deep link:', error)
        completeOAuthFlow('An error occurred during sign in')
      }
    }
  }, [completeOAuthFlow])

  useEffect(() => {
    // Prevent double initialization from StrictMode
    if (initAuthCalled.current) {
      return
    }
    initAuthCalled.current = true
    
    // Get initial session
    const initAuth = async () => {
      try {
        const { data: { session: initialSession } } = await supabase.auth.getSession()
        
        // Only update state if onAuthStateChange hasn't already done it
        if (!loadingSetByAuthChange.current) {
          setSession(initialSession)
          setUser(initialSession?.user ?? null)
        }
        
        // Ensure profile exists for already logged-in users
        if (initialSession?.user) {
          const userId = initialSession.user.id
          if (!ensuredProfiles.current.has(userId)) {
            ensuredProfiles.current.add(userId)
            // Don't await - let it run in background to not block loading
            ensureUserProfile(initialSession.user).catch(console.error)
          }
        }
      } catch (error) {
        console.error('Error getting initial session:', error)
      } finally {
        if (!loadingSetByAuthChange.current) {
          setLoading(false)
        }
      }
    }
    
    initAuth()

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, newSession) => {
        setSession(newSession)
        setUser(newSession?.user ?? null)
        loadingSetByAuthChange.current = true
        setLoading(false)
        
        // Ensure profile exists when user signs in
        // This handles new user registration and returning users without profiles
        // Don't await - let it run in background to not block other operations
        if (newSession?.user && (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED')) {
          const userId = newSession.user.id
          if (!ensuredProfiles.current.has(userId)) {
            ensuredProfiles.current.add(userId)
            ensureUserProfile(newSession.user).catch(console.error)
            // Update platform on first load (covers app opens / returning users)
            if (event === 'TOKEN_REFRESHED') {
              updateUserPlatform(userId).catch(console.warn)
            }
          }
          
          // Complete OAuth flow on successful sign in
          if (event === 'SIGNED_IN') {
            completeOAuthFlow()
            // Clear stale subscription cache from previous user
            clearSubscriptionCache()
            // Log in to RevenueCat with the user's ID
            loginPurchases(userId).catch(console.warn)
            // Track which platform the user is on
            updateUserPlatform(userId).catch(console.warn)
          }
          
          // Close the browser after successful sign in (for OAuth flows)
          if (Capacitor.isNativePlatform() && event === 'SIGNED_IN') {
            try {
              await Browser.close()
            } catch (e) {
              // Browser might already be closed or not open
            }
          }
        }
        
        // Clear the set when user signs out
        if (event === 'SIGNED_OUT') {
          ensuredProfiles.current.clear()
          // Clear subscription cache so next login starts fresh
          clearSubscriptionCache()
          // Log out from RevenueCat
          logoutPurchases().catch(console.warn)
        }
      }
    )

    // Handle deep links on native platforms
    if (Capacitor.isNativePlatform()) {
      // Handle app URL open events (deep links)
      let urlOpenListener: Awaited<ReturnType<typeof App.addListener>> | null = null
      let browserFinishedListener: Awaited<ReturnType<typeof Browser.addListener>> | null = null
      
      App.addListener('appUrlOpen', ({ url }) => {
        handleDeepLink(url)
      }).then(listener => {
        urlOpenListener = listener
      })

      // Check if app was opened with a URL
      App.getLaunchUrl().then((result) => {
        if (result?.url) {
          handleDeepLink(result.url)
        }
      })
      
      // Listen for browser closed event (user manually closed OAuth browser)
      Browser.addListener('browserFinished', () => {
        // If OAuth is still in progress when browser closes, the user likely cancelled
        // We use a small delay to allow the auth callback to complete first
        setTimeout(() => {
          // Use ref to check current state (avoids stale closure)
          if (oauthInProgressRef.current) {
            // OAuth was in progress but browser was closed without completing
            console.log('Browser closed during OAuth flow')
            completeOAuthFlow('Sign in was cancelled')
          }
        }, 500)
      }).then(listener => {
        browserFinishedListener = listener
      })

      return () => {
        subscription.unsubscribe()
        cancelOAuthTimeout()
        if (urlOpenListener) {
          urlOpenListener.remove()
        }
        if (browserFinishedListener) {
          browserFinishedListener.remove()
        }
      }
    }

    // Handle web OAuth redirect
    if (!Capacitor.isNativePlatform()) {
      // Check URL for OAuth tokens on web
      const currentUrl = window.location.href
      if (currentUrl.includes('access_token') || currentUrl.includes('code=')) {
        handleDeepLink(currentUrl)
      }
    }

    return () => {
      subscription.unsubscribe()
      cancelOAuthTimeout()
    }
  }, [handleDeepLink, completeOAuthFlow, cancelOAuthTimeout])

  const signInWithGoogle = async () => {
    // Start OAuth flow tracking
    startOAuthFlow()
    
    try {
      // Determine redirect URL based on platform
      const redirectUrl = Capacitor.isNativePlatform()
        ? 'miac://auth/callback'
        : `${window.location.origin}/auth/callback`

      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectUrl,
          skipBrowserRedirect: Capacitor.isNativePlatform(),
          queryParams: {
            prompt: 'select_account', // Force account picker even if logged in
          },
        },
      })

      if (error) {
        console.error('OAuth error:', error)
        completeOAuthFlow(error.message)
        throw error
      }

      // On native platforms, open the OAuth URL in the in-app browser
      if (Capacitor.isNativePlatform() && data.url) {
        await Browser.open({ 
          url: data.url,
          presentationStyle: getBrowserPresentationStyle(),
        })
      }
    } catch (error) {
      // Error already handled above for known OAuth errors
      // This catches unexpected errors
      if (error instanceof Error && !error.message.includes('OAuth')) {
        completeOAuthFlow('Failed to start sign in')
      }
      throw error
    }
  }

  const signInWithApple = async () => {
    // Start OAuth flow tracking
    startOAuthFlow()
    
    try {
      // Determine redirect URL based on platform
      const redirectUrl = Capacitor.isNativePlatform()
        ? 'miac://auth/callback'
        : `${window.location.origin}/auth/callback`

      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'apple',
        options: {
          redirectTo: redirectUrl,
          skipBrowserRedirect: Capacitor.isNativePlatform(),
        },
      })

      if (error) {
        console.error('OAuth error:', error)
        completeOAuthFlow(error.message)
        throw error
      }

      // On native platforms, open the OAuth URL in the in-app browser
      if (Capacitor.isNativePlatform() && data.url) {
        await Browser.open({ 
          url: data.url,
          presentationStyle: getBrowserPresentationStyle(),
        })
      }
    } catch (error) {
      // Error already handled above for known OAuth errors
      // This catches unexpected errors
      if (error instanceof Error && !error.message.includes('OAuth')) {
        completeOAuthFlow('Failed to start sign in')
      }
      throw error
    }
  }

  const signInWithEmail = async (email: string, password: string): Promise<{ error?: string }> => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      })

      if (error) {
        return { error: error.message }
      }

      setSession(data.session)
      setUser(data.session?.user ?? null)
      return {}
    } catch (error) {
      console.error('Email sign in error:', error)
      return { error: 'Failed to sign in. Please try again.' }
    }
  }

  const signUpWithEmail = async (email: string, password: string): Promise<{ error?: string; needsConfirmation?: boolean }> => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
      })

      if (error) {
        return { error: error.message }
      }

      // Check if email confirmation is required
      // If user exists but session is null, email confirmation is needed
      if (data.user && !data.session) {
        return { needsConfirmation: true }
      }

      // If we got a session, user is logged in (email confirmation disabled)
      if (data.session) {
        setSession(data.session)
        setUser(data.session?.user ?? null)
      }
      
      return {}
    } catch (error) {
      console.error('Email sign up error:', error)
      return { error: 'Failed to create account. Please try again.' }
    }
  }

  const signOut = async () => {
    try {
      // Cleanup native features (remove FCM token, etc.)
      await cleanupNativeFeatures()
      
      // Clear widget auth token so widgets can't make API calls after logout
      await clearWidgetAuthToken()
      
      const { error } = await supabase.auth.signOut()
      if (error) {
        console.error('Sign out error:', error)
        throw error
      }
      // Clear onboarding cache so next login re-checks the DB
      sessionStorage.removeItem('miac_onboarding_complete')
      setUser(null)
      setSession(null)
    } catch (error) {
      console.error('Sign out error:', error)
      throw error
    }
  }

  return (
    <AuthContext.Provider value={{ 
      user, 
      session, 
      loading, 
      oauthInProgress, 
      oauthError, 
      clearOAuthError,
      signInWithGoogle, 
      signInWithApple, 
      signInWithEmail, 
      signUpWithEmail, 
      signOut 
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
